import { CalendarClock, Loader2, Sparkles } from 'lucide-react';
import type { DraftPreviewCardData } from '../../../lib/draft-preview';
import { draftWhen } from '../../../lib/draft-preview';
import type { SendDraftComments } from '../../../lib/draft-review-store';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import { networkName, type ScheduledPostAction, type TaskContentView } from '../../../lib/task-content-view';
import { CalendarContentMark } from '../calendar/CalendarContentMark';
import { DraftPreviewCard } from '../chat-surface/MessageRow/DraftPreviewCard';
import { SocialPostPreviewCard } from '../chat-surface/MessageRow/SocialPostPreviewCard';
import { DeliveredOutcome } from './DeliveredOutcome';
import { Button, Card, Typography } from '../ui';
import styles from './TaskContentSection.module.css';

export interface TaskContentSectionProps {
  content: TaskContentView;
  draftCard?: DraftPreviewCardData;
  sendComments?: SendDraftComments;
  onReviewOpened?: () => void;
  onEditFirst?: () => void;
  onGenerate: () => void;
  generating: boolean;
  generateFailed: boolean;
  onViewPost: (url: string) => void;
  onScheduledPostTap?: (action: ScheduledPostAction) => void;
}

export function TaskContentSection({
  content,
  draftCard,
  sendComments,
  onReviewOpened,
  onEditFirst,
  onGenerate,
  generating,
  generateFailed,
  onViewPost,
  onScheduledPostTap,
}: TaskContentSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || undefined;
  if (content.state === 'idea' || content.state === 'drafting' || (content.kind === 'social' && !content.socialCard && !draftCard) || (content.kind !== 'social' && !draftCard)) {
    return (
      <div className={styles.section} data-testid="taskContentSection" data-state={content.state} data-kind={content.kind}>
        <TaskContentIdea content={content} />
        {(content.canGenerate || content.state === 'drafting' || generating) && (
          <div className={styles.generate}>
            <Button
              startIcon={<Sparkles size={16} aria-hidden />}
              loading={generating || content.state === 'drafting'}
              disabled={generating || content.state === 'drafting'}
              onClick={onGenerate}
              data-testid="taskGenerateDraft"
            >
              {generating || content.state === 'drafting' ? t('tasks.plan.generatingDraft') : t('tasks.plan.generateDraft')}
            </Button>
            {generateFailed && (
              <Typography variant="p3" component="p" color="secondary" role="alert">
                {t('tasks.plan.generateFailed')}
              </Typography>
            )}
          </div>
        )}
        {content.kind === 'social' ? (
          <SocialOutcome content={content} locale={locale} onViewPost={onViewPost} onScheduledPostTap={onScheduledPostTap} />
        ) : (
          content.state === 'done' && <DeliveredOutcome kind={content.kind} at={content.publishedAt} url={content.publishedUrl} locale={locale} onViewPost={onViewPost} />
        )}
      </div>
    );
  }
  if (content.kind === 'social' && content.socialCard) {
    return (
      <div className={styles.section} data-testid="taskContentSection" data-state={content.state} data-kind={content.kind}>
        <div className={styles.post} data-testid="taskPostPreview">
          <SocialPostPreviewCard card={content.socialCard} />
        </div>
        <SocialOutcome content={content} locale={locale} onViewPost={onViewPost} onScheduledPostTap={onScheduledPostTap} />
      </div>
    );
  }
  return (
    <div className={styles.section} data-testid="taskContentSection" data-state={content.state} data-kind={content.kind}>
      {draftCard && <DraftPreviewCard card={draftCard} sendComments={sendComments} biPage="task_details" onReviewOpened={onReviewOpened} onEditFirst={onEditFirst} flush />}
    </div>
  );
}

export function SocialOutcome({
  content,
  locale,
  onViewPost,
  onScheduledPostTap,
  tapsHeld = false,
}: {
  content: TaskContentView;
  locale: string | undefined;
  onViewPost: (url: string) => void;
  onScheduledPostTap?: (action: ScheduledPostAction) => void;
  tapsHeld?: boolean;
}) {
  const { t } = useTranslation();
  if (content.state === 'scheduled') {
    return (
      <span className={styles.outcome} data-testid="taskContentScheduledPost">
        {content.scheduledAt && (
          <span className={styles.line} data-testid="taskContentScheduled">
            <CalendarClock size={14} aria-hidden className={styles.glyph} />
            {t('draftPreview.scheduledFor', { when: draftWhen(content.scheduledAt, locale) })}
          </span>
        )}
        {onScheduledPostTap && (
          <span className={styles.actions}>
            <Button size="small" variant="outlined" disabled={tapsHeld} onClick={() => onScheduledPostTap('edit')} data-testid="taskScheduledPostEdit">
              {t('draftPreview.editFirst')}
            </Button>
            <Button size="small" variant="outlined" disabled={tapsHeld} onClick={() => onScheduledPostTap('drop')} data-testid="taskScheduledPostDrop">
              {t('draftPreview.drop')}
            </Button>
          </span>
        )}
      </span>
    );
  }
  if (content.state === 'working') {
    return (
      <span className={styles.drafting} role="status" data-testid="taskContentWorking">
        <Loader2 size={16} className={styles.spin} aria-hidden />
        <Typography variant="p2" component="span" color="secondary">
          {t('draftPreview.working')}
        </Typography>
      </span>
    );
  }
  if (content.state !== 'done') return null;
  return <DeliveredOutcome kind={content.kind} at={content.publishedAt} url={content.publishedUrl} locale={locale} onViewPost={onViewPost} />;
}

export function SocialEditFirstChip({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="outlined" size="small" onClick={onPress} data-testid="taskSocialEditFirst">
      {t('draftPreview.editFirst')}
    </Button>
  );
}

function TaskContentIdea({ content }: { content: TaskContentView }) {
  const { t, i18n } = useTranslation();
  const head =
    content.kind === 'social'
      ? [content.network ? networkName(content.network) : '', content.format ? t(`calendar.post.format.${content.format}`) : ''].filter(Boolean).join(' · ')
      : t(`calendar.post.kind.${content.kind}`);
  return (
    <Card variant="stone" size="sm" className={styles.idea} data-testid="taskContentIdea">
      <div className={styles.head}>
        <span className={styles.mark}>
          <CalendarContentMark post={{ kind: content.kind, ...(content.network ? { idea: { network: content.network } } : {}) }} size={18} />
        </span>
        {head && (
          <Typography variant="h4" component="span">
            {head}
          </Typography>
        )}
      </div>
      {content.concept && (
        <Typography variant="p2" component="p" className={styles.concept} dir={messageDir(content.concept)}>
          {content.concept}
        </Typography>
      )}
      {content.publishAt && (
        <span className={styles.line} data-testid="taskContentPlanned">
          <CalendarClock size={14} aria-hidden className={styles.glyph} />
          {t('tasks.plan.plannedFor', { when: draftWhen(content.publishAt, i18n.language || undefined) })}
        </span>
      )}
    </Card>
  );
}
